import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import en from './en.json';
import es from './es.json';
import { setFormatPrefs } from '../lib/formatPrefs';

const translations = { en, es };

export const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

const I18nContext = createContext(null);

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

function readStoredLanguage() {
  try {
    const stored = localStorage.getItem('language');
    if (stored && translations[stored]) return stored;
  } catch {
    /* ignore */
  }
  return 'en';
}

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readStoredLanguage);

  const getNestedValue = useCallback((obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }, []);

  const t = useCallback((key, params = {}) => {
    const translation = getNestedValue(translations[language], key);
    const resolved =
      translation !== undefined && translation !== null
        ? translation
        : getNestedValue(translations.en, key);

    if (resolved === undefined || resolved === null) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`Translation key not found: ${key}`);
      }
      return key;
    }

    if (typeof resolved === 'string' && Object.keys(params).length > 0) {
      return Object.entries(params).reduce(
        (str, [paramKey, value]) => str.replace(new RegExp(`{{${paramKey}}}`, 'g'), String(value)),
        resolved
      );
    }

    return resolved;
  }, [language, getNestedValue]);

  const setLanguage = useCallback((lang, options = {}) => {
    const { persist = true } = options;
    if (!translations[lang]) return;
    setLanguageState(lang);
    setFormatPrefs({ language: lang });
    if (persist) {
      localStorage.setItem('language', lang);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    setFormatPrefs({ language });
  }, [language]);

  const value = {
    language,
    setLanguage,
    t,
    languages: LANGUAGES,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export default I18nContext;
